<template>
  <transition 
    enter-active-class="transition duration-300 ease-out"
    enter-from-class="opacity-0 scale-95"
    enter-to-class="opacity-100 scale-100"
    leave-active-class="transition duration-200 ease-in"
    leave-from-class="opacity-100 scale-100"
    leave-to-class="opacity-0 scale-95"
  >
    <div 
      v-if="authStore.authModalVisible" 
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      @click.self="authStore.closeAuthModal"
    >
      <div 
        class="relative w-full max-w-md bg-white rounded-xl shadow-2xl p-6 mx-auto transform transition-all"
        @click.stop
      >
        <button 
          @click="authStore.closeAuthModal" 
          class="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div class="space-y-6">
          <h2 class="text-center text-2xl font-extrabold text-gray-900">
            {{ isVerify ? 'Confirm your email' : (isSignup ? 'Create your account' : 'Sign in to your account') }}
          </h2>

          <!-- Context message (e.g. "your selection is saved") -->
          <div v-if="authStore.authModalMessage" class="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 flex-shrink-0 mt-0.5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{{ authStore.authModalMessage }}</span>
          </div>

          <!-- Verify-by-code step -->
          <form v-if="isVerify" @submit.prevent="handleVerify" class="space-y-5">
            <p class="text-sm text-gray-600 text-center">
              We sent a 6-digit code to
              <span class="font-medium text-gray-900">{{ authStore.pendingEmail || 'your inbox' }}</span>.
              Enter it below to finish signing up.
            </p>
            <input
              id="verify-code"
              v-model="verifyCode"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              pattern="[0-9]{6}"
              required
              class="block w-full text-center tracking-[0.6em] text-2xl font-semibold py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              placeholder="000000"
            />
            <button
              type="submit"
              :disabled="verifySubmitting"
              class="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {{ verifySubmitting ? 'Verifying…' : 'Verify email' }}
            </button>
            <div class="flex items-center justify-between text-sm">
              <button type="button" @click="handleResend" :disabled="resending" class="font-medium text-blue-600 hover:text-blue-500 disabled:opacity-60">
                {{ resending ? 'Sending…' : 'Resend code' }}
              </button>
              <span v-if="resendStatus" class="text-green-600">{{ resendStatus }}</span>
            </div>
          </form>

          <form v-else @submit.prevent="handleSubmit" class="space-y-6">
            <div class="rounded-md shadow-sm -space-y-px">
              <!-- Email field -->
              <div>
                <label for="email-address" class="sr-only">Email address</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autocomplete="email"
                  required
                  v-model="email"
                  class="appearance-none rounded-t-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Email address"
                />
              </div>
              
              <!-- Password field -->
              <div>
                <label for="password" class="sr-only">Password</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  :autocomplete="isSignup ? 'new-password' : 'current-password'"
                  required
                  v-model="password"
                  class="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  :class="[isSignup ? '' : 'rounded-b-md']"
                  placeholder="Password"
                />
              </div>
              
              <!-- Confirm Password field for signup -->
              <div v-if="isSignup">
                <label for="confirm-password" class="sr-only">Confirm Password</label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autocomplete="new-password"
                  required
                  v-model="confirmPassword"
                  class="appearance-none rounded-b-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                  placeholder="Confirm Password"
                />
              </div>
            </div>

            <!-- Remember Me for login -->
            <div v-if="!isSignup" class="flex items-center justify-between">
              <div class="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  v-model="rememberMe"
                  class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label for="remember-me" class="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div class="text-sm">
                <router-link to="/auth/password-reset" @click="authStore.closeAuthModal" class="font-medium text-blue-600 hover:text-blue-500">
                  Forgot your password?
                </router-link>
              </div>
            </div>

            <!-- Terms and Conditions for signup -->
            <div v-if="isSignup" class="flex items-center">
              <input
                id="terms-and-conditions"
                name="terms"
                type="checkbox"
                required
                v-model="agreeToTerms"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="terms-and-conditions" class="ml-2 block text-sm text-gray-900">
                I agree to the
                <router-link to="/terms" target="_blank" class="text-blue-600 hover:text-blue-500">Terms and Conditions</router-link>
                and
                <router-link to="/privacy" target="_blank" class="text-blue-600 hover:text-blue-500">Privacy Policy</router-link>
              </label>
            </div>

            <div>
              <button
                type="submit"
                class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {{ isSignup ? 'Sign up' : 'Sign in' }}
              </button>
            </div>
            
            <div class="text-center">
              <p class="mt-2 text-sm text-gray-600">
                {{ isSignup ? 'Already have an account?' : 'Don\'t have an account?' }}
                <a 
                  href="#"
                  @click.prevent="toggleMode"
                  class="font-medium text-blue-600 hover:text-blue-500"
                >
                  {{ isSignup ? 'Sign in' : 'Sign up' }}
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/authStore'

const authStore = useAuthStore()

// Form fields
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const rememberMe = ref(false)
const agreeToTerms = ref(false)

// Verify-code step state
const verifyCode = ref('')
const verifySubmitting = ref(false)
const resending = ref(false)
const resendStatus = ref('')

// Determine initial mode
const isSignup = computed(() => authStore.authModalMode === 'signup')
const isVerify = computed(() => authStore.authModalMode === 'verify')

const toggleMode = () => {
  authStore.openAuthModal(isSignup.value ? 'login' : 'signup')
}

const handleVerify = async () => {
  if (verifySubmitting.value) return
  verifySubmitting.value = true
  resendStatus.value = ''
  try {
    const result = await authStore.verifyEmailCode(verifyCode.value)
    if (result.success) {
      authStore.closeAuthModal()
      verifyCode.value = ''
    } else {
      alert(formatErrors(result.errors))
    }
  } finally {
    verifySubmitting.value = false
  }
}

const handleResend = async () => {
  if (resending.value) return
  resending.value = true
  resendStatus.value = ''
  try {
    const result = await authStore.resendVerificationCode()
    if (result.success) {
      resendStatus.value = 'New code sent'
    } else {
      alert(formatErrors(result.errors))
    }
  } finally {
    resending.value = false
  }
}

const formatErrors = (errors) => {
  if (!errors) return 'Request failed. Please try again.'
  if (Array.isArray(errors)) {
    return errors.map((e) => e.message || e.param || '').filter(Boolean).join('\n') || 'Request failed.'
  }
  return String(errors)
}

const handleSubmit = async () => {
  try {
    if (isSignup.value) {
      if (password.value !== confirmPassword.value) {
        alert('Passwords do not match')
        return
      }
      if (!agreeToTerms.value) {
        alert('Please agree to the Terms and Conditions')
        return
      }

      const signupData = {
        username: email.value.split('@')[0], // Generate username from email
        email: email.value,
        password: password.value,
        agree_to_terms: agreeToTerms.value,
      }

      const result = await authStore.register(signupData)
      if (result.success) {
        // Store already flipped authModalMode to 'verify' when pending –
        // keep the modal open so the user can enter the code.
        if (!result.pendingVerification) authStore.closeAuthModal()
      } else {
        alert(formatErrors(result.errors))
      }
      return
    }

    // Login: the single field can be an email or a username.
    const result = await authStore.login({
      identifier: email.value,
      password: password.value,
    })
    if (result.success) {
      if (!result.pendingVerification) authStore.closeAuthModal()
    } else {
      alert(formatErrors(result.errors))
    }
  } catch (error) {
    console.error('Auth submit error:', error)
    alert('An unexpected error occurred. Please try again.')
  }
}
</script>

<style scoped>
/* Any additional scoped styles can be added here */
</style>
